import { Component, OnInit, signal, ViewChild, WritableSignal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatCardModule } from '@angular/material/card';
import { environment } from '../../../environments/environment';
import { ClientDetailsModel } from '../../models/ClientDetails';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BaseService } from '../../core/services/base.service';
import { NavigationTabs } from '../../shared/navigation-tabs/navigation-tabs';
import { Router } from '@angular/router';
import { CustomDateAdapter, CUSTOM_DATE_FORMATS } from '../../core/services/custom-date-adapter';
import { catchError, Observable, map, throwError } from 'rxjs';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ChangeDetectorRef } from '@angular/core';



// Define interfaces for missing models
interface UserDetailsModel {
  clientId: string;
  mobileNumber: string;
  accessFrom: string;
}

interface UserBookingModel {
  bookingId: string;
  bookingRefNo: string;
  transactionId: string;
  date: string;
  reportingTime: string;
  customerName: string;
  cancelStatus: number;
  carType: string;
  package: string;
  city: string;
  bookingStatus: string;
}

interface Booking {
  bookingId: string;
  bookingRefNo: string;
  transactionId: string;
  date: string;
  reportingTime: string;
  customerName: string;
  carType: string;
  packageName: string;
  city: string;
  bookingStatus: 'Pending for Approval' | 'Booking Confirm';
  cancelStatus: number;  // Added this property
}

@Component({
  selector: 'app-my-bookings',
  imports: [ CommonModule, FormsModule, ReactiveFormsModule, MatIconModule, MatDatepickerModule, MatFormFieldModule, MatInputModule, MatNativeDateModule, MatTableModule, MatSortModule, MatPaginatorModule, MatCardModule, NavigationTabs, MatProgressSpinnerModule],
  templateUrl: './my-bookings.html',
  styleUrls: ['./my-bookings.scss'],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
  ]
})
export class MyBookings implements OnInit {
  @ViewChild(MatSort) matSort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  private readonly authService = inject(AuthService);
  public filterUrl: string;
  IsVisible = signal<boolean>(false);
  readonly currentUser = this.authService.currentUser;
  
  constructor(
    private baseService: BaseService,
    private http: HttpClient,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private toastService: ToastService,
    private cd: ChangeDetectorRef
  ) { 
    this.filterUrl = '/assets/filter.svg';
  }

  showLoginFormOnHome() {
    console.log('Login form requested from navbar - navigating to home with login');
    // Navigate to home page with login form
    this.router.navigate(['/'], { queryParams: { showLogin: 'true' } });
  }

  showTripSearchForm() {
    console.log('Trip search form requested from navbar - navigating to home');
    // Navigate to home page
    this.router.navigate(['/']);
  }

  // Signals
  isMobile = signal<boolean>(false);
  filteredBookings = signal<Booking[]>([]);
  searchTerm = signal<string>('');
  fromDate = signal<string>('');
  toDate = signal<string>('');
  sortColumn = signal<string>('');
  sortDirection = signal<'asc' | 'desc'>('asc');
  showMobileFilter = signal<boolean>(false);
  isBookingLoading = false;

  clientDetails!: ClientDetailsModel;
  userDetails!: UserDetailsModel;
  userBookings: UserBookingModel[] = [];
  clientId: string = '';
  dataSource!: MatTableDataSource<Booking>;
  displayedColumns: string[] = ['bookingRefNo', 'transactionId', 'date', 'reportingTime', 'customerName', 'carType', 'packageName', 'city', 'bookingStatus', 'action'];

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.Handset])
      .subscribe((result: any) => {
        this.isMobile.set(result.matches);

      });
    this.GetClient();
    this.dataSource = new MatTableDataSource<Booking>([]);
  }

  computedFilteredBookings = computed(() => {
    const bookings = this.filteredBookings();
    const search = this.searchTerm();
    const from = this.fromDate();
    const to = this.toDate();
    const sortCol = this.sortColumn();
    const sortDir = this.sortDirection();

    let filtered = bookings.filter(booking => {
      const matchesSearch = !search ||
        (booking.bookingRefNo?.toString() || '').toLowerCase().includes(search.toLowerCase()) ||
        (booking.transactionId?.toString() || '').toLowerCase().includes(search.toLowerCase()) ||
        booking.customerName.toLowerCase().includes(search.toLowerCase()) ||
        booking.carType.toLowerCase().includes(search.toLowerCase()) ||
        booking.packageName.toLowerCase().includes(search.toLowerCase()) ||
        booking.reportingTime.toLowerCase().includes(search.toLowerCase()) ||
        booking.city.toLowerCase().includes(search.toLowerCase());

      const matchesDateRange = this.filterByDateRange(booking.reportingTime, from, to);

      return matchesSearch && matchesDateRange;
    });
    if (sortCol) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any = a[sortCol as keyof Booking];
        let bValue: any = b[sortCol as keyof Booking];

        if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  });

  dateForm = new FormGroup({
    fromDate: new FormControl(''),
    toDate: new FormControl('')
  });

  searchBookings() {
    // Get the current date filters
    const fromDate = this.formatDate((this.dateForm.get('fromDate'))?.value || '');
    const toDate = this.formatDate((this.dateForm.get('toDate'))?.value || '');

    const mappedBookings = this.userBookings.map(booking => ({
      ...booking,
      packageName: booking.package,
      date: this.formatDate(booking.date),
      reportingTime: booking.reportingTime,
      bookingStatus: booking.bookingStatus as 'Pending for Approval' | 'Booking Confirm'
    }));

    // Apply date filters if they exist
    const dateFilteredBookings = mappedBookings.filter(booking => {
      return this.filterByDateRange(booking.date, fromDate, toDate);
    });

    this.filteredBookings.set(dateFilteredBookings);
    this.dataSource.data = this.computedFilteredBookings();
    this.dataSource.paginator = this.paginator;
  }

  searchBookingsDetails() {
    // Just call searchBookings() since it now handles both date and text filters
    this.searchBookings();
    
    if (this.showMobileFilter() && this.isMobile()) {
      this.closeMobileFilter();
    }
  }

  filterByDateRange(dateTime: string, fromDate: string, toDate: string): boolean {
    if (!this.formatDate(fromDate) && !this.formatDate(toDate)) return true;

    const bookingDate = new Date(dateTime.split(' | ')[0]);
    const from = this.formatDate(fromDate);
    const to = this.formatDate(toDate);
    if (from && bookingDate < new Date(from)) return false;
    if (to && bookingDate > new Date(to)) return false;
    return true;
  }

  sort(column: string) {

    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
    this.dataSource.data = this.computedFilteredBookings();
  }

  updateDateFilters(from: string, to: string) {
    this.fromDate.set(from);
    this.toDate.set(to);
    this.dataSource.data = this.computedFilteredBookings();
  }

  openMobileFilter() {
    this.showMobileFilter.set(true);
    this.IsVisible.set(true);

  }

  closeMobileFilter() {
    this.showMobileFilter.set(false);
    this.IsVisible.set(false);
  }

  applyMobileFilter() {
    // Apply the filter and close the modal
    this.searchBookingsDetails();
    this.closeMobileFilter();
  }

  onTabChange(tab: 'available-cabs' | 'my-bookings') {
    if (tab === 'available-cabs') {
      // Navigate to available cabs page
      console.log('Navigate to Available Cabs');
      // Add your navigation logic here
    } else if (tab === 'my-bookings') {
      // Already on my bookings page
      console.log('Already on My Bookings page');
    }
  }

  GetClient() {

    
    const apiUrl = `/api/B2COnlineBooking/B2CclientDetails`;
    console.log('TripSearchService loadCities API URL:', apiUrl);

    this.baseService.getData(apiUrl).pipe(
      map((response: any) => {
        console.log('TripSearchService loadCities response:', response);
        if (response.responseCode === 'OK') {
          this.clientDetails = response.data as ClientDetailsModel;
          this.GetUserBookingsDetails();
          return response.data as ClientDetailsModel[];
        } else {
          throw new Error(response.responseMessage || 'Failed to load cities');
        }
      }),
      catchError(this.handleError)
    ).subscribe({
      next: (data) => {
        console.log('Client details loaded successfully');
      },
      error: (error) => {
        console.error('Failed to load client details:', error);
      }
    });
  }

  private handleError(error: any): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  GetUserBookingsDetails() {
    debugger;
    const user = this.currentUser();
    this.userDetails = {
      clientId: this.clientDetails.clientId.toString(),
      mobileNumber: user?.phone || '',
      accessFrom: 'IsLogin'
    };

    const apiUrl = '/api/B2COnlineBooking/GetUserBookingList';
    this.baseService.postData(apiUrl, this.userDetails).subscribe({
      next: (res: any) => {
        if (res.responseCode === 'OK') {
   
          this.userBookings = res.data as UserBookingModel[];
          const mappedBookings = this.userBookings.map(booking => ({
            ...booking,
            packageName: booking.package,
            date: this.formatDate(booking.date),
            reportingTime: booking.reportingTime,
            bookingStatus: booking.bookingStatus as 'Pending for Approval' | 'Booking Confirm',
            cancelStatus: booking.cancelStatus
          })).sort((a, b) => {
            const dateA = new Date(a.date.split('-').reverse().join('-'));
            const dateB = new Date(b.date.split('-').reverse().join('-'));
            if (dateA.getTime() !== dateB.getTime()) {
              return dateB.getTime() - dateA.getTime();
            }
            return b.reportingTime.localeCompare(a.reportingTime);
          });
        
          console.log('mappedBookings', mappedBookings);
          


          this.filteredBookings.set(mappedBookings);
          this.dataSource.data = this.computedFilteredBookings();
          this.dataSource.sort = this.matSort;
          this.dataSource.paginator = this.paginator;

        }
      },
      error: (err: any) => {
        console.error('API call failed:', err);
      }
    });
  }

  cancelBooking(bookingId: string) {
    if (confirm('Are you sure you want to cancel this booking?')) {
      this.isBookingLoading = true;
      const apiUrl = '/api/B2COnlineBooking/' + bookingId + '/CancelBooking';
      this.baseService.getData(apiUrl).subscribe({
        next: (res: any) => {
          this.toastService.success(res.responseMessage, 'Cancel Booking');
          this.GetUserBookingsDetails();
          this.isBookingLoading = false;
        },
        error: (err: any) => {
          debugger;
          this.isBookingLoading = false;
          this.cd.detectChanges();
          if (err.status === 400) {
            this.toastService.error(err.error.responseMessage, 'Cancel Booking');
          } else {
            this.toastService.error(err.error.responseMessage, 'Cancel Booking');
          }
          this.isBookingLoading = false;
        }
      });
    }
  }



}

